import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAppLayoutContextConsumer } from '@occc-app/design-system';
import { useGetCampaignName, getCreatePageLinks } from '@occc-app/routes/CampaignComponents/utils';
import { Offer_Engagement_Type } from '@occc/graphql';
import { OfferType } from '@occc-app/offerValidationService/types';

import { useChainId } from '@occc-app/Chain/hooks';
import { useNavigateToCampaign } from '@occc-app/offerHelpers/hooks/useNavigateToCampaign';

import { OfferCreateConfigurePage } from '@occc-app/routes/Offer/OfferCreatePage/OfferCreateConfigurePage';
import { CampaignComponentTabs } from '@occc-app/routes/CampaignComponents/components/ConfigurationLayout';
import { Flow_Types } from '@occc-app/offerHelpers/types';
import { SweepstakesConfigureFormInitialValues, SweepstakesConfigureFormValues } from '../types';
import { ConfigureForm } from '../ConfigureForm';
import { getConfigureFormOperations } from '../getConfigureFormOperations';
import { routeCreator, transformToInitialValues } from '../utils';
import { messages } from '../messages';

export const SweepstakesCreatePageContainer = () => {
  const navigate = useNavigate();
  const { id: campaignId = '' } = useParams<{ id: string }>();

  const chainId = useChainId();
  const campaignName = useGetCampaignName(chainId, campaignId);
  const navigateToCampaign = useNavigateToCampaign(campaignId);

  const links = useMemo(
    () => getCreatePageLinks(campaignId, campaignName, 'New Sweepstakes'),
    [campaignId, campaignName],
  );
  useAppLayoutContextConsumer(links);

  return (
    <OfferCreateConfigurePage<SweepstakesConfigureFormValues, SweepstakesConfigureFormInitialValues>
      pageTitle='Campaign > Create'
      subtitle={messages.subtitle}
      offerType={OfferType.Sweepstakes}
      engagementType={Offer_Engagement_Type.SweepstakesTransactional}
      campaignId={campaignId}
      getConfigureOperations={getConfigureFormOperations}
      getInitialValues={transformToInitialValues}
      onOfferCreated={(offerId) =>
        navigate(routeCreator.configureWithTab(campaignId, offerId, CampaignComponentTabs.Target))
      }
      renderConfigureForm={({ onSubmit, initialValues, loading }) => (
        <ConfigureForm
          onSubmit={onSubmit}
          initialValues={initialValues}
          loading={loading}
          onCancelClick={navigateToCampaign}
          currentFlowType={Flow_Types.Configure}
          isFormEditable
        />
      )}
    />
  );
};
